/**
 * Privacy Policy text — single source of truth.
 *
 * The same content is rendered in two places:
 *   - PrivacyPolicyModal (opens overlaid on the register form so the visitor
 *     never loses the data they already typed)
 *   - /politica-de-privacidade page (linked from the global footer)
 *
 * Rendered by `<PrivacyPolicyBody />` (a tiny stateless component below). We
 * keep the markup here — not in markdown — to avoid bringing a markdown
 * renderer dependency for a single static document. When the legal copy is
 * updated, edit this file only.
 */

export const PRIVACY_POLICY_VERSION = "1.0";
export const PRIVACY_POLICY_LAST_UPDATED = "[a definir na publicação oficial]";

export function PrivacyPolicyBody() {
  return (
    <div className="privacy-policy-body space-y-5 text-[#3D3D3D] text-sm leading-relaxed">
      <header className="pb-4 border-b border-[#E8E4DF]">
        <p className="overline text-[#B8A88A] mb-2">Prize Joias</p>
        <h2 className="font-display text-2xl md:text-3xl mb-2">Política de Privacidade</h2>
        <p className="text-xs text-[#A8A8A8]">
          Versão {PRIVACY_POLICY_VERSION} — {PRIVACY_POLICY_LAST_UPDATED}
        </p>
      </header>

      <p>
        Esta Política de Privacidade descreve como a Prize Joias (“nós”, “nossa loja”) coleta,
        usa, armazena e protege os dados pessoais dos clientes e visitantes do nosso site, em
        conformidade com a Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018 — LGPD).
      </p>

      <div className="bg-[#FAF9F7] border border-[#E8E4DF] p-4 text-xs text-[#6B6B6B] space-y-1">
        <p><strong>Razão social:</strong> [inserir razão social completa]</p>
        <p><strong>CNPJ:</strong> [inserir CNPJ]</p>
        <p><strong>Endereço:</strong> [inserir endereço completo]</p>
        <p><strong>Contato para assuntos de privacidade:</strong> [inserir e-mail, ex: privacidade@prizejoias.com.br]</p>
      </div>

      <Section title="1. Quais dados coletamos">
        <p>
          Coletamos os seguintes tipos de dados pessoais, dependendo de como você interage com nosso site:
        </p>
        <h4 className="font-medium mt-4 mb-2">1.1. Dados de cadastro</h4>
        <ul className="list-disc pl-5 space-y-1">
          <li>Nome completo</li>
          <li>E-mail</li>
          <li>Telefone/WhatsApp</li>
          <li>CPF</li>
          <li>Endereço de entrega (CEP, logradouro, número, complemento, bairro, cidade, estado)</li>
          <li>Senha de acesso (armazenada de forma criptografada)</li>
        </ul>
        <h4 className="font-medium mt-4 mb-2">1.2. Dados de compra</h4>
        <ul className="list-disc pl-5 space-y-1">
          <li>Histórico de pedidos, valores e formas de pagamento utilizadas</li>
          <li>
            Dados necessários para processamento de pagamento, processados por nosso parceiro de
            pagamentos (Mercado Pago); não armazenamos dados completos de cartão de crédito em
            nossos servidores
          </li>
        </ul>
        <h4 className="font-medium mt-4 mb-2">1.3. Dados de navegação e comportamento</h4>
        <ul className="list-disc pl-5 space-y-1">
          <li>Páginas e produtos visualizados durante sua navegação no site</li>
          <li>Produtos adicionados à lista de desejos (favoritos)</li>
          <li>Interações com nosso quiz de estilo e com a funcionalidade de experimentação virtual (“Try On”)</li>
          <li>Informações técnicas básicas de acesso (endereço IP, tipo de navegador, cookies de sessão)</li>
        </ul>
      </Section>

      <Section title="2. Para que usamos seus dados">
        <p>Utilizamos os dados pessoais coletados para as seguintes finalidades:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Processar seu cadastro, login e autenticação na loja</li>
          <li>Processar pedidos, pagamentos, emissão de nota fiscal e entrega das joias adquiridas</li>
          <li>Enviar comunicações transacionais, como confirmação de cadastro, confirmação de pedido e atualizações sobre o status da compra</li>
          <li>
            Personalizar sua experiência de navegação, recomendando produtos com maior afinidade
            ao seu perfil de estilo e histórico de interesse — com base nas categorias, estilos e
            faixas de preço das peças que você visualiza ou favorita
          </li>
          <li>Cumprir obrigações legais e regulatórias, incluindo fiscais e de defesa do consumidor</li>
          <li>Prevenir fraudes e garantir a segurança das transações</li>
        </ul>
        <p className="mt-3 italic text-[#6B6B6B]">
          Nosso sistema de recomendação personalizada sempre combina seu perfil de interesse com
          um fator de variedade aleatória, de modo que você continue descobrindo novas peças e
          estilos, e não apenas conteúdo repetitivo baseado unicamente em seu histórico.
        </p>
      </Section>

      <Section title="3. Com quem compartilhamos seus dados">
        <p>
          Podemos compartilhar dados pessoais com terceiros nas seguintes situações, sempre
          limitando o compartilhamento ao estritamente necessário:
        </p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Processador de pagamentos (Mercado Pago), para viabilizar a cobrança e confirmação das transações</li>
          <li>Provedor de envio de e-mails transacionais, para entrega de confirmações de cadastro e de compra</li>
          <li>Transportadoras e serviços de logística, para realizar a entrega dos produtos adquiridos</li>
          <li>Autoridades públicas, quando exigido por lei, ordem judicial ou requisição de autoridade competente</li>
        </ul>
        <p className="mt-3 font-medium">
          Não vendemos nem alugamos seus dados pessoais a terceiros para fins de marketing de terceiros.
        </p>
      </Section>

      <Section title="4. Armazenamento e retenção de dados">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Dados de cadastro e de pedidos são mantidos enquanto sua conta estiver ativa e, após
            o encerramento, pelo prazo necessário para cumprimento de obrigações legais e fiscais
          </li>
          <li>
            Dados de navegação e comportamento (visualizações de produto, sinais de interesse)
            são mantidos por até 6 (seis) meses, sendo automaticamente eliminados após esse período
          </li>
        </ul>
        <p className="mt-3">
          Adotamos medidas técnicas e organizacionais razoáveis para proteger seus dados contra
          acesso não autorizado, perda, alteração ou divulgação indevida.
        </p>
      </Section>

      <Section title="5. Seus direitos como titular de dados">
        <p>Em conformidade com a LGPD, você tem direito a:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Confirmar a existência de tratamento de seus dados</li>
          <li>Acessar os dados pessoais que mantemos sobre você</li>
          <li>Corrigir dados incompletos, inexatos ou desatualizados</li>
          <li>Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade com a lei</li>
          <li>Solicitar a portabilidade de seus dados a outro fornecedor de serviço</li>
          <li>Eliminar os dados pessoais tratados com seu consentimento, exceto nas hipóteses legais de retenção obrigatória</li>
          <li>Revogar o consentimento previamente fornecido</li>
          <li>Resetar ou apagar, a qualquer momento, seu perfil de recomendação personalizada, por meio de opção disponível na sua área de conta</li>
        </ul>
        <p className="mt-3">
          Para exercer qualquer um desses direitos, entre em contato pelo e-mail
          {" "}<em>[inserir e-mail de contato]</em>. Responderemos sua solicitação dentro do prazo
          legal aplicável.
        </p>
      </Section>

      <Section title="6. Cookies">
        <p>
          Utilizamos cookies essenciais para o funcionamento do site, como manutenção do
          carrinho de compras e da sua sessão de login. Também podemos utilizar cookies para
          fins de personalização de recomendações de produtos, conforme descrito na Seção 2
          desta Política.
        </p>
      </Section>

      <Section title="7. Try On (experimentação virtual) e dados de imagem">
        <p>
          Ao utilizar a funcionalidade de experimentação virtual (“Try On”), você envia uma
          foto sua para que nosso sistema gere uma simulação de como a joia escolhida ficaria
          em você, com o auxílio de inteligência artificial. Essa imagem é utilizada
          exclusivamente para a geração da simulação solicitada e não é utilizada para outras
          finalidades, nem compartilhada com terceiros além do provedor de tecnologia de
          geração de imagem estritamente necessário para processar a solicitação.
        </p>
      </Section>

      <Section title="8. Alterações nesta Política">
        <p>
          Esta Política de Privacidade pode ser atualizada periodicamente, para refletir
          mudanças em nossas práticas ou na legislação aplicável. Recomendamos a revisão
          periódica desta página. Alterações relevantes serão comunicadas por e-mail ou por
          aviso destacado em nosso site.
        </p>
      </Section>

      <Section title="9. Contato">
        <p>
          Em caso de dúvidas sobre esta Política de Privacidade ou sobre o tratamento de seus
          dados pessoais, contate-nos pelo e-mail <em>[inserir e-mail de contato]</em> ou pelo
          telefone <em>[inserir telefone de contato]</em>.
        </p>
      </Section>

      <p className="text-xs italic text-[#A8A8A8] pt-4 border-t border-[#E8E4DF]">
        Este documento é um modelo de referência e não substitui a revisão por um advogado
        especializado em proteção de dados antes da publicação oficial.
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="font-display text-base md:text-lg font-medium mb-2 mt-1">{title}</h3>
      {children}
    </section>
  );
}
